import DateTimeDisplay from '@/components/DateTime';
import { currencyFormatter } from '@/helpers/currencyFormatter';
import { StandardHoldbackSelfServiceClientData } from '@/types/Holdbacks';
import { Box, Card, Column, Table } from '@palmetto/palmetto-components';
import { memo, useMemo } from 'react';

interface ArchivedStandardHoldbacksProps {
  holdbacks: StandardHoldbackSelfServiceClientData[];
  isLoading: boolean;
}

export const ArchivedStandardHoldbacks = memo(({ holdbacks, isLoading }: ArchivedStandardHoldbacksProps) => {
  const columns = useMemo(
    () => [
      {
        heading: 'State',
        dataKey: 'state',
        render: (cell: string) => cell,
      },
      {
        heading: 'Organization Name',
        dataKey: 'organizationName',
        render: (cell: string) => cell,
      },
      {
        heading: 'Amount',
        dataKey: 'amount',
        render: (cell: number) => currencyFormatter.format(cell),
      },
      {
        heading: 'Created',
        render: (_cell: unknown, row: StandardHoldbackSelfServiceClientData) => {
          const dateValue = row.createdBy?.date ?? row.createdDate;
          return dateValue ? (
            <DateTimeDisplay value={dateValue.toString()} showDuration={false} dateFormat="DD h:mm:ss a" inline />
          ) : (
            '---'
          );
        },
      },
      {
        heading: 'Created By',
        dataKey: 'createdBy',
        // Note: Optional chaining used because createdBy field may be undefined in "next" environment
        render: (cell: StandardHoldbackSelfServiceClientData['createdBy']) => cell?.user?.name || '---',
      },
      {
        heading: 'Archived',
        dataKey: 'archivedDate',
        render: (cell: Date) => {
          return cell ? (
            <DateTimeDisplay value={cell.toString()} showDuration={false} dateFormat="DD h:mm:ss a" inline />
          ) : (
            '---'
          );
        },
      },
      {
        heading: 'Archived By',
        dataKey: 'archivedBy',
        render: (cell: StandardHoldbackSelfServiceClientData['archivedBy']) => cell?.name || '---',
      },
    ],
    [],
  );
  return (
    <Card padding="xs">
      <Box padding="lg">
        <Box fontWeight="medium" fontSize="lg">
          Archived
        </Box>
      </Box>
      <Box margin="0 md">
        <Table
          rowKey="id"
          columns={columns as Column[]}
          rows={holdbacks}
          align="left"
          isScrollable={{
            x: false,
            y: true,
          }}
          isLoading={isLoading}
        />
      </Box>
    </Card>
  );
});

ArchivedStandardHoldbacks.displayName = 'ArchivedStandardHoldbacks';
